import { useEffect, useState } from "react";
import { useBackState } from "@/hooks/useScrollMemory";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRoles, useRoleNames } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { OneTimeReveal } from "@/components/OneTimeReveal";
import { ConfirmAction, resetTotpConfirm, deactivateConfirm, archiveConfirm } from "@/components/ConfirmAction";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, ShieldCheck, UserX, UserCheck, KeyRound, RotateCcw, Copy, ChevronRight, Lock, Unlock, Archive, ArchiveRestore, GraduationCap } from "lucide-react";



export default function UsersPage() {
  const roles = useRoles();
  const roleNames = useRoleNames();
  const { user: me, permissions } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const lockedParam = (params.get("locked") || "").toLowerCase();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "", password: "", home_site_id: "" });
  const [sites, setSites] = useState([]);
  const [policy, setPolicy] = useState({ min_length: 12, blocklist_size: 0 });
  const [saving, setSaving] = useState(false);
  const [resetReveal, setResetReveal] = useState(null);
  const [resetting, setResetting] = useState(false);
  // GR-40: the one confirmation box for every action on this page that TAKES ACCESS AWAY.
  const [pending, setPending] = useState(null);
  const [showArchived, setShowArchived] = useBackState("showArchived", false);
  const [archivedCount, setArchivedCount] = useState(0);
  const [archivingSample, setArchivingSample] = useState(false);

  const refreshArchivedCount = async () => {
    try { const { data } = await api.get("/admin/users", { params: { archived: true } }); setArchivedCount(data.length); }
    catch { /* */ }
  };

  const load = async () => {
    try { const { data } = await api.get("/admin/users", { params: { archived: showArchived } }); setUsers(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    refreshArchivedCount();
  };
  useEffect(() => {
    load();
    api.get("/password-policy").then(({ data }) => setPolicy(data)).catch(() => {});
    api.get("/admin/sites").then(({ data }) => setSites(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const archiveSample = async () => {
    if (!window.confirm("Archive all seeded test accounts (sales/foreman/parts/writer/tech)? They'll be hidden, their logins disabled, and fully restorable later. Your admin and any real users you created are untouched.")) return;
    setArchivingSample(true);
    try {
      const { data } = await api.post("/admin/users/archive-sample");
      toast.success(data.archived_count ? `Archived ${data.archived_count} test account(s): ${data.emails.join(", ")}` : "No sample test accounts to archive.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setArchivingSample(false); }
  };

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created — they'll enroll 2FA at first login");
      setOpen(false); setForm({ email: "", name: "", role: "", password: "", home_site_id: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const act = async (u, action, label) => {
    try { await api.post(`/admin/users/${u.id}/${action}`); toast.success(label); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  /* GR-40 — the three actions that TAKE ACCESS AWAY ask first, through the one shared box
     (ConfirmAction.jsx). Named handlers on purpose: a multi-line onClick in this file was
     mis-bound by the dev build and fired the inner callback immediately. */
  const askResetTotp = (u) => setPending(resetTotpConfirm(u, u.email === me?.email, () => act(u, "reset-totp", "2FA reset — re-enroll at next login")));
  const askDeactivate = (u) => setPending(deactivateConfirm(u, () => act(u, "deactivate", "User deactivated — sessions ended")));
  const askArchive = (u) => setPending(archiveConfirm(u, () => act(u, "archive", "User archived — hidden & login disabled")));

  const resetPassword = async (u) => {
    if (u.email === me?.email) return;
    if (!window.confirm(`Reset password for ${u.email}? Their current sessions end and they must set a new password at next login.`)) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setResetReveal({ email: u.email, password: data.temp_password });
      // NO list refresh while the reveal is open. THAT refresh is the bug of 2026-10-01: on a
      // self-reset it returned 401, the interceptor redirected, and the one-time passphrase was
      // destroyed before it could be read. It now runs when the dialog is closed, not behind it.
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setResetting(false); }
  };

  const closeReveal = () => { setResetReveal(null); load(); };

  // P4 — clears the failed-attempt counter everywhere for that person. Nothing else.
  const clearLockout = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/clear-lockout`);
      toast.success(data.message || "Lockout cleared");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const canClear = !!permissions?.clear_login_lockout;
  const shown = lockedParam ? users.filter((u) => (u.email || "").toLowerCase() === lockedParam) : users;

  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Users</h1>
          <p className="text-sm text-slate-500">Admin-only · click a user to manage · forced 2FA enrollment</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="toggle-archived-users" variant="outline" onClick={() => setShowArchived((v) => !v)}
            className={`rounded-lg border-slate-200 ${showArchived ? "bg-slate-100" : ""}`}>
            <Archive className="w-4 h-4 mr-1.5" />{showArchived ? "Show active" : `Show archived (${archivedCount})`}
          </Button>
          {!showArchived && (
            <Button data-testid="archive-sample-users" variant="outline" disabled={archivingSample} onClick={archiveSample}
              className="rounded-lg border-slate-200 text-destructive">
              {archivingSample ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Archive className="w-4 h-4 mr-1.5" />}Archive sample users
            </Button>
          )}
          <Button data-testid="new-user-button" onClick={() => setOpen(true)} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
            <Plus className="w-4 h-4 mr-1.5" /> New user
          </Button>
        </div>
      </div>

      {showArchived && (
        <div data-testid="archived-banner" className="flex items-center gap-2 mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <Archive className="w-3.5 h-3.5 shrink-0" />Viewing <b className="mx-1">archived</b> users — logins are disabled. Restore any to bring them back (un-archived &amp; re-activated).
        </div>
      )}

      {lockedParam && (
        <div data-testid="locked-filter-banner" className="flex items-center gap-2 mb-3 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <Lock className="w-3.5 h-3.5 shrink-0" />
          {shown.length
            ? <span>Opened from a failed-login alert — showing <b className="mx-1">{lockedParam}</b> only.</span>
            : <span>The alert named <b className="mx-1">{lockedParam}</b>, and <b className="mr-1">no account here uses that address</b> — the attempts were against an email that doesn't exist in this system.</span>}
          <button data-testid="locked-filter-clear" onClick={() => { params.delete("locked"); setParams(params); }}
            className="underline ml-2">show all users</button>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="password-privacy-note">
        <Lock className="w-3.5 h-3.5 shrink-0" />
        Passwords are hashed and <b className="mx-1">never viewable by anyone</b> — by design. You can only <b className="mx-1">reset</b> them, which gives the user a one-time temporary password to set their own.
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100 text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">
              <th className="py-2 px-3 border-r border-slate-200">Email</th>
              <th className="py-2 px-3 border-r border-slate-200">Name</th>
              <th className="py-2 px-3 border-r border-slate-200">Role</th>
              <th className="py-2 px-3 border-r border-slate-200" title="Effective access (role + per-user override)">Access</th>
              <th className="py-2 px-3 border-r border-slate-200">Status</th>
              <th className="py-2 px-3 border-r border-slate-200">2FA</th>
              <th className="py-2 px-3 border-r border-slate-200">Last login</th>
              <th className="py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="users-table-body">
            {shown.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-t border-slate-200 hover:bg-orange-50/40 cursor-pointer" onClick={() => navigate(`/users/${u.id}`)}>
                <td className="py-2 px-3 border-r border-slate-100 font-mono text-xs">
                  <span data-testid={`open-user-${u.email}`} className="text-[#C76714] hover:underline flex items-center gap-1">
                    {u.email}<ChevronRight className="w-3 h-3" />
                  </span>
                </td>
                <td className="py-2 px-3 border-r border-slate-100">{u.name}</td>
                <td className="py-2 px-3 border-r border-slate-100">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge className={`rounded-lg text-[10px] ${u.role === "Admin" ? "bg-[#1F1F1F] text-white" : "bg-slate-200 text-slate-700"}`}>{u.role_name || u.role}</Badge>
                    {u.review_graduated && <Badge data-testid={`graduated-badge-${u.email}`} className="rounded-lg text-[10px] bg-emerald-700 text-white"><GraduationCap className="w-3 h-3 mr-0.5" />review-exempt</Badge>}
                  </div>
                </td>
                <td className="py-2 px-3 border-r border-slate-100">
                  <div className="flex items-center gap-1" data-testid={`access-chips-${u.email}`}>
                    {[
                      { k: "view_parts", label: "Scan", title: "Scan / item context (scanned part → its donor's parts)" },
                      { k: "search_inventory", label: "Search", title: "Search & browse all inventory" },
                      { k: "use_desk_surface", label: "Desk", title: "Use the Desk surface (Desk ↔ Floor)" },
                    ].map(({ k, label, title }) => {
                      const on = !!(u.access && u.access[k]);
                      return (
                        <span key={k} title={title}
                          data-testid={`access-${k}-${u.email}-${on ? "on" : "off"}`}
                          className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${on ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-400 line-through"}`}>
                          {label}
                        </span>
                      );
                    })}
                  </div>
                </td>
                <td className="py-2 px-3 border-r border-slate-100">
                  {u.archived ? <Badge className="rounded-lg bg-slate-500 text-white text-[10px]">ARCHIVED</Badge>
                    : u.active ? <Badge className="rounded-lg bg-green-600 text-white text-[10px]">ACTIVE</Badge>
                    : <Badge className="rounded-lg bg-destructive text-white text-[10px]">DEACTIVATED</Badge>}
                </td>
                <td className="py-2 px-3 border-r border-slate-100">
                  {u.totp_enrolled ? <span className="text-green-600 font-mono text-xs flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />on</span>
                    : <span className="text-slate-400 font-mono text-xs">pending</span>}
                </td>
                <td className="py-2 px-3 border-r border-slate-100 font-mono text-[10px] text-slate-500">{(u.last_login_at || "—").slice(0, 19).replace("T", " ")}</td>
                <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    {/* Absent unless there is a live lock AND the viewer can clear it — no greyed
                        button telling people about a control they can't touch. */}
                    {canClear && u.lockout?.locked && (
                      <Button data-testid={`clear-lockout-${u.email}`} size="sm" variant="outline"
                        className="rounded-lg h-7 border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                        onClick={() => clearLockout(u)}
                        title={u.lockout.places > 1 ? `Locked in ${u.lockout.places} places — clearing fixes all of them` : "Clear the failed-attempt counter — password and 2FA are untouched"}>
                        <Unlock className="w-3.5 h-3.5 mr-1" />
                        Locked — {u.lockout.minutes_left} min left · Clear
                      </Button>
                    )}
                    {u.archived ? (
                      <Button data-testid={`restore-${u.email}`} size="sm" variant="outline"
                        className="rounded-lg h-7 border-slate-200 text-green-700" onClick={() => act(u, "restore", "User restored — un-archived & reactivated")} title="Restore">
                        <ArchiveRestore className="w-3.5 h-3.5 mr-1" />Restore
                      </Button>
                    ) : (
                      <>
                        {u.active
                          ? <Button data-testid={`deactivate-${u.email}`} size="sm" variant="outline" disabled={u.role === "Admin" || u.email === me?.email}
                              className="rounded-lg h-7 border-slate-200 text-destructive" title="Deactivate"
                              onClick={() => askDeactivate(u)}>
                              <UserX className="w-3.5 h-3.5" />
                            </Button>
                          : <Button data-testid={`activate-${u.email}`} size="sm" variant="outline"
                              className="rounded-lg h-7 border-slate-200 text-green-700" onClick={() => act(u, "activate", "User reactivated")} title="Reactivate">
                              <UserCheck className="w-3.5 h-3.5" />
                            </Button>}
                        {/* GR-40: this fired with NO confirmation at all, on your own row too. */}
                        <Button data-testid={`reset-totp-${u.email}`} size="sm" variant="outline" className="rounded-lg h-7 border-slate-200" title="Reset 2FA"
                          onClick={() => askResetTotp(u)}>
                          <KeyRound className="w-3.5 h-3.5" />
                        </Button>
                        <Button data-testid={`reset-password-${u.email}`} size="sm" variant="outline" disabled={resetting || u.email === me?.email} className="rounded-lg h-7 border-slate-200 text-[#E0781A]" onClick={() => resetPassword(u)}
                          title={u.email === me?.email
                            ? "Not for your own account — it would end your session before you could save the temporary password. Use My Password & 2FA Codes to change your own."
                            : "Reset password"}>
                          <RotateCcw className="w-3.5 h-3.5" />
                        </Button>
                        <Button data-testid={`archive-${u.email}`} size="sm" variant="outline" disabled={u.role === "Admin" || u.email === me?.email}
                          className="rounded-lg h-7 border-slate-200 text-slate-600" title="Archive"
                          onClick={() => askArchive(u)}>
                          <Archive className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg border-slate-200 sm:max-w-md" data-testid="new-user-dialog">
          <DialogHeader><DialogTitle className="font-display">New user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
              <Input data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg border-slate-200 mt-1 font-mono" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Name</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg border-slate-200 mt-1" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="rounded-lg border-slate-200 mt-1"><SelectValue placeholder="Choose a role" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  {roles.map((r) => <SelectItem key={r} value={r} data-testid={`role-opt-${r.replace(/ /g, "-").toLowerCase()}`}>{roleNames[r] || r}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Home site</Label>
              <Select value={form.home_site_id || "none"} onValueChange={(v) => setForm({ ...form, home_site_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="user-home-site-select" className="rounded-lg border-slate-200 mt-1"><SelectValue placeholder="All sites (no home)" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="none">All sites (no home)</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ""}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Temporary password</Label>
              <Input data-testid="user-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-lg border-slate-200 mt-1 font-mono" />
              <p className="text-[11px] text-slate-500 mt-1 font-mono">min {policy.min_length} chars · checked vs {policy.blocklist_size}-entry breach list · no forced rotation</p></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-user-button" disabled={saving || !form.email || !form.password || !form.name.trim() || !form.role} onClick={create} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ONE shared reveal for every shown-once secret in the app — see OneTimeReveal.jsx.
          Note what is NOT here: a list refresh. That refresh is what destroyed a one-time
          passphrase on 2026-10-01; it now happens in closeReveal, when the dialog is dismissed. */}
      <OneTimeReveal
        open={!!resetReveal}
        title="Temporary password"
        intro={resetReveal ? `Share this once with ${resetReveal.email} by phone or text. They must change it at next login.` : ""}
        value={resetReveal?.password}
        onClose={closeReveal}
        testid="reset-password-dialog" />

      {/* ONE confirmation for Reset 2FA / Deactivate / Archive — shared with the user detail
          page, wording and typed-RESET gate included. Activate and Restore stay one press. */}
      <ConfirmAction pending={pending} onClose={() => setPending(null)} />
    </div>
  );
}
